import { replayMarks, type DocSnapshot, type Mark, type ParamSpec } from '@flobi/artwork-core/plugin';
import { HostError, type PluginInstance } from '@flobi/artwork-core/plugin-host';
import type { Rail, Tool, ToolContext, Well } from '../tools/types';
import type { AddonBridge } from './bridge';

/**
 * An add-on, in hand as a tool.
 *
 * It is an ordinary editor `Tool` — the same contract the built-in ones keep — wrapped
 * round a sandbox. So the canvas needs to know nothing about add-ons: a press is a
 * press, the overlay is drawn in the one place overlays are drawn, and the rail is read
 * the way every rail is read.
 *
 * Two things it does that the design rests on:
 *
 * - The document goes over ONCE per gesture. The snapshot is built on the press and
 *   handed to every handler of that gesture by identity, so the sandbox recognises it and
 *   does not rebuild anything. Rebuilding it per move cost three frames on a big page.
 * - Marks are RETAINED. What a handler returned is replayed every frame until another
 *   handler returns something new, so a still hand crosses nothing at all.
 */

export interface AddonToolOptions {
  readonly pluginId: string;
  readonly versionHash: string;
  readonly instance: PluginInstance;
  readonly bridge: () => AddonBridge | null;
  /** The person's current answers to the add-on's params. */
  readonly params: () => Record<string, unknown>;
  readonly setParam: (name: string, value: unknown) => void;
  /** Something went wrong that the person needs to hear about. */
  readonly onProblem: (message: string) => void;
  /** It ran and changed the page. */
  readonly onRun?: (changed: number) => void;
  /**
   * A deadline was missed: records it and answers with how many this add-on has missed
   * this session. Counted by whoever picks tools up, because each pick-up is a fresh tool
   * (QA 2026-09-24, library-addons-F20).
   */
  readonly missed?: () => number;
}

/** How many deadline misses in a session before a tool is taken out of hand for good. */
export const MISSES_ALLOWED = 3;

export function addonTool(options: AddonToolOptions): Tool {
  const { instance } = options;
  const manifest = instance.manifest;
  const has = new Set(manifest.handlers);

  let marks: readonly Mark[] = [];
  /** The snapshot of the gesture in progress, handed over by identity. */
  let gesture: DocSnapshot | null = null;
  let hoverSnapshot: DocSnapshot | null = null;
  let hoverVersion = -1;
  let sceneVersion = 0;
  let pressed = false;
  let misses = 0;
  let unsubscribe: (() => void) | null = null;

  /** A handler that blew a limit: say so, and put the arrow back rather than freeze. */
  const fail = (ctx: ToolContext, error: unknown) => {
    pressed = false;
    gesture = null;
    marks = [];
    instance.forget();
    if (error instanceof HostError && error.code === 'timed_out') {
      misses = options.missed ? options.missed() : misses + 1;
      options.onProblem(
        misses >= MISSES_ALLOWED
          ? `${manifest.name} kept running too long and has been put down for this session.`
          : `${manifest.name} took too long and was stopped.`,
      );
    } else {
      options.onProblem(`${manifest.name} failed: ${error instanceof Error ? error.message : String(error)}`);
    }
    ctx.refresh();
    ctx.chooseTool('select');
  };

  const run = (ctx: ToolContext, phase: 'hover' | 'down' | 'move' | 'up', at: { x: number; y: number }, snapshot: DocSnapshot) => {
    const bridge = options.bridge();
    if (!bridge) return null;
    return instance.runTool(phase, { snapshot, host: bridge.host, at, params: options.params() });
  };

  const tool: Tool = {
    id: 'addon',

    activate(ctx) {
      if (misses >= MISSES_ALLOWED) {
        ctx.chooseTool('select');
        return;
      }
      ctx.setCursor(manifest.cursor ?? 'crosshair');
      // A hover snapshot is only good for as long as the page has not changed under it.
      unsubscribe = ctx.scene.subscribe(() => {
        sceneVersion++;
      });
    },

    deactivate(ctx) {
      // Nothing is left behind, which every built-in tool has to promise by hand and
      // this one cannot fail to: the gesture is forgotten and the marks go with it.
      if (pressed) instance.forget();
      pressed = false;
      gesture = null;
      hoverSnapshot = null;
      marks = [];
      unsubscribe?.();
      unsubscribe = null;
      ctx.setCursor(null);
    },

    onPointerDown(_event, world, ctx) {
      const bridge = options.bridge();
      if (!bridge) return false;
      pressed = true;
      gesture = bridge.snapshot(manifest.reads);
      if (!has.has('down')) return true;
      try {
        const out = run(ctx, 'down', world, gesture);
        if (out) marks = out.marks;
        // A press answered with only a line — Grid fill with nothing chosen — is the tool
        // saying why the drag will do nothing (QA 2026-09-24, library-addons-F6).
        if (out && out.marks.length === 0 && out.logs[0]) options.onProblem(out.logs[0]);
        ctx.refresh();
      } catch (error) {
        fail(ctx, error);
      }
      return true;
    },

    onPointerMove(event, world, ctx) {
      try {
        if (pressed && gesture) {
          if (!has.has('move')) return;
          const out = run(ctx, 'move', world, gesture);
          if (out) marks = out.marks;
          ctx.refresh();
          return;
        }
        if (event.buttons !== 0 || !has.has('hover')) return;
        const bridge = options.bridge();
        if (!bridge) return;
        if (!hoverSnapshot || hoverVersion !== sceneVersion) {
          hoverSnapshot = bridge.snapshot(manifest.reads);
          hoverVersion = sceneVersion;
        }
        const out = run(ctx, 'hover', world, hoverSnapshot);
        if (out) marks = out.marks;
        ctx.refresh();
      } catch (error) {
        fail(ctx, error);
      }
    },

    onPointerUp(_event, world, ctx) {
      if (!pressed || !gesture) return;
      pressed = false;
      const snapshot = gesture;
      gesture = null;
      try {
        const out = has.has('up') ? run(ctx, 'up', world, snapshot) : null;
        marks = [];
        if (out && out.ops.length > 0) {
          const bridge = options.bridge();
          const applied = bridge?.apply(out, { pluginId: options.pluginId, versionHash: options.versionHash, ms: out.ms });
          if (applied && !applied.ok) options.onProblem(`${manifest.name} asked for something the page cannot do: ${applied.message}`);
          else if (applied?.ok) options.onRun?.(applied.changed);
        } else if (out?.logs[0]) {
          // A release that changed nothing says why, the way a command does: a tool's
          // lines were never read (library-addons-F6).
          options.onProblem(out.logs[0]);
        }
        ctx.refresh();
      } catch (error) {
        fail(ctx, error);
      }
    },

    onKeyDown(event, ctx) {
      if (!pressed) return false;
      // Undo and Delete wait until the hand is up. Mid-drag they could take away the very
      // shape the gesture's snapshot names, and Grid fill then left an empty 'Grid' behind
      // on release (QA 2026-09-24, library-addons-F18).
      const meta = event.metaKey || event.ctrlKey;
      if (event.key === 'Backspace' || event.key === 'Delete' || (meta && (event.key.toLowerCase() === 'z' || event.key.toLowerCase() === 'y'))) {
        event.preventDefault();
        return true;
      }
      // Escape abandons the gesture and keeps the tool, which is what Escape means
      // mid-drag in every other tool.
      if (event.key !== 'Escape') return false;
      pressed = false;
      gesture = null;
      marks = [];
      instance.forget();
      ctx.refresh();
      return true;
    },

    draw(overlay) {
      if (marks.length > 0) replayMarks(overlay, marks);
    },

    rail(): Rail | null {
      // The params that asked for a place on the rail, in the slot they asked for. The
      // rest live in the drawer's form, where there is room for them.
      const values = options.params();
      const slot = (name: 'top' | 'middle' | 'bottom'): Well | undefined => {
        const found = Object.entries(manifest.params).find(([, spec]) => spec.rail === name);
        if (!found) return undefined;
        return wellFor(found[0], found[1], values[found[0]], options.setParam);
      };
      const top = slot('top');
      const middle = slot('middle');
      const bottom = slot('bottom');
      if (!top && !middle && !bottom) return null;
      return { ...(top ? { top } : {}), ...(middle ? { middle } : {}), ...(bottom ? { bottom } : {}) };
    },
  };
  return tool;
}

/** A param as a rail well. Only a number has a well; anything else stays in the drawer. */
function wellFor(name: string, spec: ParamSpec, value: unknown, set: (name: string, value: unknown) => void): Well | undefined {
  if (spec.type !== 'number') return undefined;
  const current = typeof value === 'number' ? value : spec.default;
  return {
    kind: 'slider',
    icon: 'adjust',
    title: spec.label,
    value: current,
    min: spec.min ?? 0,
    max: spec.max ?? Math.max(100, spec.default * 2),
    ...(spec.step !== undefined ? { step: spec.step } : {}),
    format: (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1)),
    onChange: (v: number) => set(name, v),
  };
}
